import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Plus, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [zones, setZones] = useState([]);
  const [products, setProducts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedZone, setSelectedZone] = useState('');
  const [formData, setFormData] = useState({
    zone_id: '',
    surface_item: '',
    product_id: '',
    dosage: '',
    temperature_instruction: '',
    method_id: '',
    frequency_id: '',
    control: '',
    responsible: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedZone) {
      loadTemplates(selectedZone);
    } else {
      setTemplates([]);
    }
  }, [selectedZone]);

  const loadData = async () => {
    try {
      const [zonesRes, productsRes, methodsRes, freqRes] = await Promise.all([
        api.get('/zones'),
        api.get('/products'),
        api.get('/methods'),
        api.get('/frequencies'),
      ]);
      setZones(zonesRes.data);
      setProducts(productsRes.data);
      setMethods(methodsRes.data);
      setFrequencies(freqRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async (zoneId) => {
    try {
      const { data } = await api.get(`/templates?zone_id=${zoneId}`);
      setTemplates(data);
    } catch (error) {
      toast.error('Failed to load templates');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/templates', formData);
      toast.success('Template line created');
      resetForm();
      if (selectedZone) loadTemplates(selectedZone);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const resetForm = () => {
    setFormData({
      zone_id: selectedZone,
      surface_item: '',
      product_id: '',
      dosage: '',
      temperature_instruction: '',
      method_id: '',
      frequency_id: '',
      control: '',
      responsible: '',
    });
    setShowForm(false);
  };

  const handleAddTemplate = () => {
    if (!selectedZone) {
      toast.error('Please select a zone first');
      return;
    }
    setFormData({ ...formData, zone_id: selectedZone });
    setShowForm(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="templates-page">
        <h1 className="text-3xl font-bold mb-8" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
          Template Management
        </h1>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
            Select Zone
          </label>
          <select
            value={selectedZone}
            onChange={(e) => setSelectedZone(e.target.value)}
            data-testid="zone-select"
            className="w-full max-w-md px-4 py-2 border rounded"
            style={{ borderColor: '#D1D5DB' }}
          >
            <option value="">-- Select a zone --</option>
            {zones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
        </div>

        {selectedZone && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold" style={{ color: '#0E2D43' }}>
                {zones.find((z) => z.id === selectedZone)?.name} Templates
              </h2>
              {!showForm && (
                <button
                  onClick={handleAddTemplate}
                  data-testid="add-template-button"
                  className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}
                >
                  <Plus size={20} />
                  <span>Add Template Line</span>
                </button>
              )}
            </div>

            {showForm && (
              <div className="bg-white rounded-lg shadow p-6 border mb-8" style={{ borderColor: '#E5E7EB' }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: '#0E2D43' }}>
                  New Template Line
                </h3>
                <form onSubmit={handleSubmit} data-testid="template-form">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                        Surface/Equipment *
                      </label>
                      <input
                        type="text"
                        value={formData.surface_item}
                        onChange={(e) => setFormData({ ...formData, surface_item: e.target.value })}
                        data-testid="template-surface-input"
                        className="w-full px-4 py-2 border rounded"
                        style={{ borderColor: '#D1D5DB' }}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                        Product
                      </label>
                      <select
                        value={formData.product_id}
                        onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                        data-testid="template-product-select"
                        className="w-full px-4 py-2 border rounded"
                        style={{ borderColor: '#D1D5DB' }}
                      >
                        <option value="">Select product</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                        Dosage
                      </label>
                      <input
                        type="text"
                        value={formData.dosage}
                        onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        style={{ borderColor: '#D1D5DB' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                        Temperature
                      </label>
                      <input
                        type="text"
                        value={formData.temperature_instruction}
                        onChange={(e) => setFormData({ ...formData, temperature_instruction: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        style={{ borderColor: '#D1D5DB' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                        Method
                      </label>
                      <select
                        value={formData.method_id}
                        onChange={(e) => setFormData({ ...formData, method_id: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        style={{ borderColor: '#D1D5DB' }}
                      >
                        <option value="">Select method</option>
                        {methods.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                        Frequency
                      </label>
                      <select
                        value={formData.frequency_id}
                        onChange={(e) => setFormData({ ...formData, frequency_id: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        style={{ borderColor: '#D1D5DB' }}
                      >
                        <option value="">Select frequency</option>
                        {frequencies.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                        Control
                      </label>
                      <input
                        type="text"
                        value={formData.control}
                        onChange={(e) => setFormData({ ...formData, control: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        style={{ borderColor: '#D1D5DB' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                        Responsible
                      </label>
                      <input
                        type="text"
                        value={formData.responsible}
                        onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        style={{ borderColor: '#D1D5DB' }}
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      data-testid="save-template-button"
                      className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                      style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}
                    >
                      <Save size={18} />
                      <span>Save</span>
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex items-center space-x-2 px-4 py-2 rounded border"
                      style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}
                    >
                      <X size={18} />
                      <span>Cancel</span>
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
              <table className="w-full">
                <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Surface</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Dosage</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Frequency</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                        No templates for this zone yet.
                      </td>
                    </tr>
                  ) : (
                    templates.map((template) => {
                      const product = products.find((p) => p.id === template.product_id);
                      const method = methods.find((m) => m.id === template.method_id);
                      const freq = frequencies.find((f) => f.id === template.frequency_id);
                      return (
                        <tr key={template.id} data-testid={`template-row-${template.id}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                          <td className="px-4 py-3" style={{ color: '#0E2D43' }}>
                            {template.surface_item}
                          </td>
                          <td className="px-4 py-3" style={{ color: '#6B7280' }}>
                            {product?.name || '-'}
                          </td>
                          <td className="px-4 py-3" style={{ color: '#6B7280' }}>
                            {template.dosage || '-'}
                          </td>
                          <td className="px-4 py-3" style={{ color: '#6B7280' }}>
                            {method?.name || '-'}
                          </td>
                          <td className="px-4 py-3" style={{ color: '#6B7280' }}>
                            {freq?.name || '-'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Templates;
